/**
 * Box plot. Shows the distribution of `encoding.yKey`. Multiple boxes come from
 * either `encoding.groupKey` (one box per category found in the data, colored
 * via `groupColors`) or `series[]` (one box per series column). With neither, a
 * single box of the `yKey` column is drawn. `groupColors` only colors; the
 * category set always comes from the data.
 */

import type { ChartTypeDefinition, ChartTrace } from '../types';
import { registerChart } from '../registry';
import { colorAt } from '../theme/palette';
import { toFiniteNumber } from '../shared/number';
import { uniqueValues } from '../shared/uniqueValues';

/** Numeric values of `key` across rows, with blank/non-numeric cells dropped. */
function numericValues(rows: { [k: string]: unknown }[], key: string): number[] {
  return rows
    .map((r) => toFiniteNumber(r[key]))
    .filter((v): v is number => v !== null);
}

export const boxChart: ChartTypeDefinition = {
  id: 'box',
  label: 'Box Plot',
  capabilities: {
    multiSeries: true,
    supportsXKey: false,
    supportsYKey: true,
    supportsGroupBy: true,
    supportsLimits: true,
    filterable: true,
    supportsStacked: false,
    legendByDefault: false,
  },
  buildTraces(model, ctx) {
    const { data, encoding, series, groupColors, labels } = model;
    const yKey = encoding.yKey ?? 'y';
    const groupKey = encoding.groupKey;

    if (groupKey) {
      const categories = uniqueValues(data, groupKey);

      return categories.map((cat, i): ChartTrace => {
        const points = data.filter((d) => String(d[groupKey]) === cat);
        return {
          y: numericValues(points, yKey),
          type: 'box',
          name: cat,
          marker: { color: groupColors[cat] || colorAt(i, ctx.palette) },
        };
      });
    }

    if (series.length > 0) {
      return series.map((s, i): ChartTrace => {
        return {
          y: numericValues(data, s.dataKey),
          type: 'box',
          name: s.name,
          marker: { color: s.color || colorAt(i, ctx.palette) },
        };
      });
    }

    return [
      {
        y: numericValues(data, yKey),
        type: 'box',
        name: labels.title ?? yKey,
        marker: { color: colorAt(0, ctx.palette) },
      },
    ];
  },
};

registerChart(boxChart);
